import React from 'react';
import { useClickableCard } from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { LargeImageCard } from '@leagueplatform/masonry-widgets';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { MasonryProgressBarCardContentRenderer } from './masonry-progress-bar-card-content-renderer.component';
import { MasonryProgressBarCardRendererType } from './masonry-progress-bar-card-renderer.type';
import { ConditionalLinkCardRenderer } from '../common/conditional-link-card-renderer/conditional-link-card-renderer.component';
import {
  MasonryDeeplinkAction,
  MasonryDismissAction,
  MasonryDriverAction,
} from '../../types/masonry-driver-node-actions';

type MasonryProgressBarCardNodeProperties =
  MasonryProgressBarCardRendererType & {
    primaryActionRef?: React.RefObject<HTMLAnchorElement>;
    isDismissible?: boolean;
  };

export type MasonryProgressBarCardNodeActions = {
  onClick?: MasonryDriverAction;
  onDismiss?: MasonryDismissAction;
};

export type MasonryProgressBarCardNode = MasonryEngineNode<
  'progressBar',
  MasonryProgressBarCardNodeProperties,
  MasonryProgressBarCardNodeActions
>;

export type MasonryProgressBarCardCardRendererProps =
  MasonryEngineNodeRendererProps<MasonryProgressBarCardNode>;

const MasonryProgressBarCardWithoutImage = ({
  eyebrow,
  heading,
  currentText,
  progress,
  suffixText,
  footerText,
  emphasizeCurrentValue,
  emphasizeFooterText,
  onClick,
  isDismissible,
  primaryActionRef,
  headingLevel,
  tooltipIcon,
  tooltipLabel,
  tooltipContent,
}: Omit<MasonryProgressBarCardCardRendererProps, 'rootElementRef'> & {
  primaryActionRef?: React.RefObject<HTMLAnchorElement>;
}) => (
  <MasonryProgressBarCardContentRenderer
    eyebrow={eyebrow}
    heading={heading}
    currentText={currentText}
    progress={progress}
    suffixText={suffixText}
    footerText={footerText}
    linkRefState={primaryActionRef}
    emphasizeCurrentValue={emphasizeCurrentValue}
    emphasizeFooterText={emphasizeFooterText}
    onClick={onClick}
    isDismissible={isDismissible}
    headingLevel={headingLevel}
    tooltipIcon={tooltipIcon}
    tooltipLabel={tooltipLabel}
    tooltipContent={tooltipContent}
  />
);

const MasonryProgressBarCardWithImage = ({
  eyebrow,
  heading,
  image,
  imageAltText = '',
  currentText,
  progress,
  suffixText,
  footerText,
  onClick,
  isDismissible,
  isChildItem = false,
  emphasizeCurrentValue,
  emphasizeFooterText,
  primaryActionRef,
  headingLevel,
  tooltipIcon,
  tooltipLabel,
  tooltipContent,
  tooltipTitle,
}: Omit<MasonryProgressBarCardCardRendererProps, 'rootElementRef'> &
  Required<Pick<MasonryProgressBarCardCardRendererProps, 'image'>> & {
    primaryActionRef?: React.RefObject<HTMLAnchorElement>;
  }) => (
  <LargeImageCard
    image={image}
    imageAltText={imageAltText}
    isChildItem={isChildItem}
  >
    <MasonryProgressBarCardContentRenderer
      eyebrow={eyebrow}
      heading={heading}
      currentText={currentText}
      progress={progress}
      suffixText={suffixText}
      footerText={footerText}
      linkRefState={primaryActionRef}
      emphasizeCurrentValue={emphasizeCurrentValue}
      emphasizeFooterText={emphasizeFooterText}
      onClick={onClick}
      isDismissible={isDismissible}
      headingLevel={headingLevel}
      tooltipIcon={tooltipIcon}
      tooltipLabel={tooltipLabel}
      tooltipContent={tooltipContent}
      tooltipTitle={tooltipTitle}
    />
  </LargeImageCard>
);

export const MasonryProgressBarCardRenderer = ({
  isChildItem,
  className,
  engineClassName,
  locatorId,
  css,
  rootElementRef,
  ...props
}: MasonryProgressBarCardCardRendererProps) => {
  const isDismissible = Boolean(props.onDismiss);
  const { image, onClick } = props;
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);

  return (
    <ConditionalLinkCardRenderer
      hasLink={Boolean((onClick as MasonryDeeplinkAction)?.payload.url)}
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      onDismiss={props.onDismiss}
      hasFloatingDismissButton
      imagePosition={
        isChildItem
          ? 'top'
          : {
              '@initial': 'start',
              '@mobile': 'top',
            }
      }
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      {image ? (
        <MasonryProgressBarCardWithImage
          isChildItem={isChildItem}
          image={image}
          primaryActionRef={primaryActionRef}
          onClick={onClick}
          isDismissible={isDismissible}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      ) : (
        <MasonryProgressBarCardWithoutImage
          primaryActionRef={primaryActionRef}
          onClick={onClick}
          isDismissible={isDismissible}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    </ConditionalLinkCardRenderer>
  );
};
